import React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import googleLogo from '@/assets/images/logos/google-logo.png'
import { LoginRequestDTO } from '@/core/api/dtos/request/auth/login'
import { useTranslation } from '@/core/i18n/useTranslation'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/atoms/Alert'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { H2 } from '@/shared/components/atoms/H2'
import { Input } from '@/shared/components/atoms/Input'
import { Link } from '@/shared/components/atoms/Link'
import { PasswordInput } from '@/shared/components/atoms/PasswordInput'
import { Spinner } from '@/shared/components/atoms/Spinner'

import { OnbardingLayout } from '../OnbardingLayout'

interface LoginPageUIProps {
	isLoading: boolean
	error: string | null
	form: UseFormReturn<LoginRequestDTO>
	onFinish: (values: LoginRequestDTO) => void
	handleGoogleLogin: () => void
	googleAuthEnabled?: boolean
}

export const LoginPageUI: React.FC<LoginPageUIProps> = ({
	isLoading,
	error,
	form,
	onFinish,
	handleGoogleLogin,
	googleAuthEnabled = false,
}) => {
	// Initialize translations with auth namespace
	const { t } = useTranslation('auth')
	const { t: tCommon } = useTranslation('common')

	return (
		<OnbardingLayout hideImageOnStep={false}>
			<div className="flex justify-center">
				<div className="mt-4" style={{ width: '400px' }}>
					<H2 className="flex">{t('login.title')}</H2>

					<Spinner spinning={isLoading}>
						{error && (
							<Alert variant="error" className="mb-2 mt-2">
								<AlertTitle>{tCommon('common.error')}</AlertTitle>
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}

						<form onSubmit={form.handleSubmit(onFinish)} noValidate>
							<div className="space-y-4">
								<div>
									<label className="text-xs mb-2 block">
										{t('login.email')}
									</label>
									<Controller
										control={form.control}
										name="email"
										render={({ field, fieldState: { error } }) => (
											<div>
												<Input
													placeholder={t('login.email').toLowerCase()}
													className="h-10"
													{...field}
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>

								<div>
									<label className="text-xs mb-2 block">
										{t('login.password')}
									</label>
									<Controller
										control={form.control}
										name="password"
										render={({ field, fieldState: { error } }) => (
											<div>
												<PasswordInput
													placeholder={t('login.password')}
													className="h-10"
													{...field}
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>
							</div>

							<div className="flex flex-col justify-center gap-2 mt-3">
								<div className="mb-0 w-full">
									<Button
										className="h-10 px-8 py-3 cursor-pointer w-full text-sm font-medium rounded-full"
										disabled={isLoading || !form.formState.isValid}
										type="submit"
									>
										{t('login.submit')}
									</Button>
								</div>

								{googleAuthEnabled && (
									<Button
										type="button"
										onClick={handleGoogleLogin}
										className="h-10 px-8 py-3 mt-2 cursor-pointer w-full text-sm font-medium rounded-full"
									>
										<img
											src={googleLogo}
											alt="Google"
											className="inline-block mr-2 w-4 h-4 align-middle"
										/>
										{t('register.google')}
									</Button>
								)}
							</div>
							<div className="flex justify-center gap-1 mt-4">
								{t('login.forgotPassword')}
								<Link to="/forgot-password">{t('login.clickHere')}</Link>
							</div>
						</form>
					</Spinner>

					<div className="text-center text-sm mt-4">
						{t('login.noAccount')}{' '}
						<Link to="/register">{t('login.signUp')}</Link>
					</div>
				</div>
			</div>
		</OnbardingLayout>
	)
}
